import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createInitiative, resetCreateState } from "../../../redux/initiatives";
import { toast } from "react-toastify";
import moment from "moment";
import { format } from "date-fns";

const CreateInitiativeModal = ({ open, onClose, users }) => {
  const dispatch = useDispatch();
  const { create } = useSelector((state) => state.Initiatives);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    owner_id: null,
    secondary_owner_id: null,
    status: "future",
    priority: "medium",
    objective_completion_date: null,
  });

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleOwnerChange = (field) => (event, value) => {
    setFormData({ ...formData, [field]: value?.id || null });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, objective_completion_date: date });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.owner_id) {
      toast.error("Owner is required");
      return;
    }

    const data = {
      ...formData,
      objective_completion_date: formData.objective_completion_date
        ? format(new Date(formData.objective_completion_date), "yyyy-MM-dd")
        : null,
    };

    const result = await dispatch(createInitiative(data));
    if (result.meta.requestStatus === "fulfilled") {
      toast.success("Initiative created successfully");
      handleClose();
    } else {
      toast.error(result.payload || "Failed to create initiative");
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      description: "",
      owner_id: null,
      secondary_owner_id: null,
      status: "future",
      priority: "medium",
      objective_completion_date: null,
    });
    dispatch(resetCreateState());
    onClose();
  };

  const getSelectedUser = (userId) => {
    return users.find((u) => u.id === userId) || null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Initiative</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title}
              onChange={handleChange("title")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange("description")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.full_name || ""}
              value={getSelectedUser(formData.owner_id)}
              onChange={handleOwnerChange("owner_id")}
              renderInput={(params) => (
                <TextField {...params} label="Owner" required />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.full_name || ""}
              value={getSelectedUser(formData.secondary_owner_id)}
              onChange={handleOwnerChange("secondary_owner_id")}
              renderInput={(params) => (
                <TextField {...params} label="Secondary Owner" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={handleChange("status")}
              >
                <MenuItem value="future">Future</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="working">Working</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={handleChange("priority")}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Target Completion Date"
                value={formData.objective_completion_date}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={create.isLoading}
        >
          {create.isLoading ? <CircularProgress size={24} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInitiativeModal;
